import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '../utils/api';
import { formatCurrency, getDatePreset } from '../utils/formatters';
import DateRangeFilter from '../components/DateRangeFilter';
import ExportButton from '../components/ExportButton';
import ChartCard from '../components/ChartCard';

export default function Affiliates() {
  const [daily, setDaily] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dateRange, setDateRange] = useState(getDatePreset('last_30d'));
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/affiliates', { params: dateRange });
      setDaily(data.daily || []);
      setSummary(data.summary || null);
    } catch (err) { console.error('Affiliates fetch error:', err); }
    setLoading(false);
  }, [dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const affRate = summary && summary.total_orders > 0
    ? ((summary.affiliate_orders / summary.total_orders) * 100).toFixed(1)
    : '0.0';

  const totalCommission = (summary?.total_affiliate_commission || 0) + (summary?.total_affiliate_ads_commission || 0);

  // Chart data — chronological, skip days with no affiliate activity
  const chartData = [...daily].reverse()
    .filter(d => d.affiliate_orders > 0)
    .map(d => ({
      date: d.date?.substring(5),
      commission: d.affiliate_commission || 0,
      adsCommission: d.affiliate_ads_commission || 0,
      gmv: d.affiliate_gmv || 0,
    }));

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Affiliates</h2>
        <ExportButton endpoint="/api/affiliates/export" params={dateRange} filename="affiliates-export.csv" />
      </div>
      <DateRangeFilter onChange={setDateRange} initial="last_30d" />

      {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}><div className="spinner" /></div>}

      {!loading && <><div className="kpi-grid" style={{ marginBottom: '16px' }}>
        <div className="kpi-card">
          <div className="kpi-label">Affiliate Orders</div>
          <div className="kpi-value">{summary?.affiliate_orders || 0}</div>
          <div className="kpi-sub">{affRate}% of {summary?.total_orders || 0} total</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Affiliate GMV</div>
          <div className="kpi-value">{formatCurrency(summary?.affiliate_gmv)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Commission</div>
          <div className="kpi-value">{formatCurrency(totalCommission)}</div>
          <div className="kpi-sub">
            {formatCurrency(summary?.total_affiliate_commission)} creator + {formatCurrency(summary?.total_affiliate_ads_commission)} ads
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Avg Commission / Order</div>
          <div className="kpi-value">{summary?.affiliate_orders > 0 ? formatCurrency(totalCommission / summary.affiliate_orders) : '$0.00'}</div>
        </div>
      </div>

      {chartData.length > 1 && (
        <div style={{ marginBottom: '16px' }}>
          <ChartCard title="Affiliate GMV & Commission by Day">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => v >= 1000 ? `$${(v / 1000).toLocaleString()}k` : `$${v.toLocaleString()}`} />
                <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, undefined]} />
                <Legend />
                <Bar dataKey="gmv" name="Affiliate GMV" fill="#22c55e" />
                <Bar dataKey="commission" name="Creator Commission" fill="#6366f1" stackId="comm" />
                <Bar dataKey="adsCommission" name="Ads Commission" fill="#f59e0b" stackId="comm" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th style={{ textAlign: 'right' }}>Aff. Orders</th>
                <th style={{ textAlign: 'right' }}>Aff. GMV</th>
                <th style={{ textAlign: 'right' }}>Creator Comm.</th>
                <th style={{ textAlign: 'right' }}>Ads Comm.</th>
                <th style={{ textAlign: 'right' }}>Total Comm.</th>
              </tr>
            </thead>
            <tbody>
              {daily.filter(d => d.affiliate_orders > 0).map((d, i) => (
                <tr key={i}>
                  <td>{d.date}</td>
                  <td style={{ textAlign: 'right' }}>{d.affiliate_orders}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(d.affiliate_gmv)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(d.affiliate_commission)}</td>
                  <td style={{ textAlign: 'right' }}>{formatCurrency(d.affiliate_ads_commission)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency((d.affiliate_commission || 0) + (d.affiliate_ads_commission || 0))}</td>
                </tr>
              ))}
              {daily.filter(d => d.affiliate_orders > 0).length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-light)' }}>No affiliate orders for this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      </>}
    </div>
  );
}
